import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Table, Space, Modal, Row, Col, Tooltip, Input, Tag, Form, Select, Checkbox } from 'antd';
import { EditOutlined, DeleteOutlined, ReloadOutlined, PlusCircleOutlined, FilterOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isEmpty, debounce } from 'underscore';
import dayjs from 'dayjs';
import 'dayjs/locale/lt';
import 'moment/locale/lt';

import { AppDispatch, CombinedStates } from '../../core/types';
import { Exam } from '../../core/exam/types';
import Main from '../../components/layout/Main';
import { getExams, createExam, updateExam, deleteExam } from '../../core/exam/examApi';
import { loadSingleExam, resetSingleExam } from '../../core/exam/examSlice';

import './examsListing.scss';

const examTypeOptions = [
    { value: 'vbe', label: 'VBE', color: 'blue' },
    { value: 'tbd', label: 'TBD', color: 'green' },
    { value: 'brandos_darbas', label: 'Brandos darbas', color: 'orange' },
];

const ExamsListing = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isFilterVisible, setIsFilterVisible] = useState<boolean>(false);
    const [filters, setFilters] = useState({
        type: [] as string[],
        is_active: [] as boolean[],
        is_steam: [] as boolean[],
    });
    const [form] = Form.useForm();

    const {
        exams,
        isFetching,
        singleExam,
    } = useSelector((state: CombinedStates) => state.exam);

    const handleCreate = () => {
        form
            .validateFields()
            .then(values => {
                console.log(values);
                if (singleExam?.id) {
                    dispatch(updateExam({
                        id: singleExam.id,
                        ...values,
                    }));
                } else {
                    dispatch(createExam(values));
                }
                setIsModalVisible(false);
                form.resetFields();
                dispatch(resetSingleExam());
            })
            .catch(info => {
                console.log('Validacijos klaida:', info);
            });
    };

    const handleEdit = (id: number) => {
        dispatch(loadSingleExam(id));
        setIsModalVisible(true);
    };

    useEffect(() => {
        if (singleExam && isModalVisible) {
            form.setFieldsValue({
                ...singleExam,
            });
        }
    }, [singleExam, isModalVisible]);

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        dispatch(resetSingleExam());
    };

    const debouncedSearch = useCallback(
        debounce((value: string) => {
            if (value.trim() !== '') {
                dispatch(getExams({ search: value }));
            }
        }, 1000),
        [],
    );

    // Trigger fetch on component mount
    useEffect(() => {
        if (isEmpty(exams)) {
            dispatch(getExams({}));
        }
    }, []);

    const doSearch = (term: string) => {
        setSearchTerm(term);

        const trimmed = term.trim();

        if (trimmed === '') {
            // Čia tik tada, kai prieš tai buvo kažkas įvesta
            dispatch(getExams({}));
        } else {
            debouncedSearch(term);
        }
    };

    const toolbar = [{ label: 'Item 1', link: '/abc', iconName: 'home' }];
    const navigation = [{ label: 'Item 1', link: '/abc' }, { label: 'Item 1', link: '/abc' }];

    const handleDelete = (exam: Exam) => {
        Modal.confirm({
            title: 'Patvirtinkite veiksmą',
            content: 'Ar tikrai norite ištrinti egzaminą?',
            okText: 'Taip, ištrinti',
            cancelText: 'Atšaukti',
            okType: 'danger',
            onOk() {
                dispatch(deleteExam({ id: exam.id }));
            },
        });
    };

    // Local filtering function
    const getFilteredExams = () => {
        let filteredExams = [...exams];

        // Apply search filter
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filteredExams = filteredExams.filter(exam => exam.title.toLowerCase().includes(searchLower)
                || exam.code.toLowerCase().includes(searchLower));
        }

        // Apply type filter
        if (filters.type.length > 0) {
            filteredExams = filteredExams.filter(exam => filters.type.includes(exam.type));
        }

        // Apply is_active filter
        if (filters.is_active.length > 0) {
            filteredExams = filteredExams.filter(exam => {
                // Handle both boolean and number/string representations
                const examIsActive = Boolean(exam.is_active);
                return filters.is_active.some(filterValue => Boolean(filterValue) === examIsActive);
            });
        }

        // Apply is_steam filter
        if (filters.is_steam.length > 0) {
            filteredExams = filteredExams.filter(exam => {
                // Handle both boolean and number/string representations
                const examIsSteam = Boolean(exam.is_steam);
                return filters.is_steam.some(filterValue => Boolean(filterValue) === examIsSteam);
            });
        }

        return filteredExams;
    };

    const filteredExams = getFilteredExams();

    return (
        <>
            <Main
                className="data-listing"
                title={<div>Egzaminų administravimas</div>}
                headerTitle={<div>Egzaminų administravimas</div>}
                menuWidth={350}
                menuPlacement="left"
                toolbar={toolbar}
                showFooter={false}
                floatingHeader={false}
                footerContent={<div />}
                floatingFooter
                backLink={false}
                breadcrumb={null}>
                <Card style={{ marginTop: '20px' }}>
                    <Row className="listing-table-title" justify="space-between" align="middle">
                        <Col className="listing-table-title-left">
                            <h3 style={{ marginBottom: 0 }}>Egzaminai</h3>
                            <span style={{ fontWeight: 500 }}>
                                Iš viso: {filteredExams.length}
                            </span>
                        </Col>
                        <Col>
                            <Space>
                                <Button icon={<FilterOutlined />} onClick={() => setIsFilterVisible(true)}>
                                    Filtras
                                </Button>
                                <Input
                                    placeholder="Paieška..."
                                    value={searchTerm}
                                    onChange={(e) => doSearch(e.target.value)} />
                                <Tooltip title="Atnaujinti sąrašą">
                                    <Button
                                        icon={<ReloadOutlined />}
                                        onClick={() => { dispatch(getExams({})); setSearchTerm(''); }} />
                                </Tooltip>
                                <Button loading={isFetching} icon={<PlusCircleOutlined />} type="primary" onClick={() => { dispatch(resetSingleExam()); setIsModalVisible(true); }}>
                                    Pridėti naują egzaminą
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                    <Table
                        dataSource={filteredExams}
                        loading={isFetching}
                        scroll={{ x: 'max-content' }}
                        rowKey="id"
                        columns={[
                            {
                                title: 'Id',
                                dataIndex: 'id',
                                key: 'id',
                            },
                            {
                                title: 'Pavadinimas',
                                dataIndex: 'title',
                                key: 'title',
                                width: 250,
                                render: (text: string) => (
                                    <Tooltip title={text}>
                                        <div style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: 250,
                                        }}>
                                            {text}
                                        </div>
                                    </Tooltip>
                                ),
                            },
                            {
                                title: 'Kodas',
                                dataIndex: 'code',
                                key: 'code',
                                render: (text: string) => (
                                    <Tooltip title={text}>
                                        <div style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: 150,
                                        }}>
                                            {text}
                                        </div>
                                    </Tooltip>
                                ),
                            },
                            {
                                title: 'Tipas',
                                dataIndex: 'type',
                                key: 'type',
                                render: (value: string) => {
                                    const option = examTypeOptions.find((opt) => opt.value === value);
                                    return option ? <Tag color={option.color}>{option.label}</Tag> : value;
                                },
                            },
                            {
                                title: 'Aktyvus',
                                dataIndex: 'is_active',
                                key: 'is_active',
                                render: (value: boolean) => (value ? <Tag color="green">Taip</Tag> : <Tag color="red">Ne</Tag>),
                            },
                            {
                                title: 'STEAM',
                                dataIndex: 'is_steam',
                                key: 'is_steam',
                                render: (value: boolean) => (value ? <Tag color="purple">Taip</Tag> : <Tag color="default">Ne</Tag>),
                            },
                            {
                                title: 'Sukurta',
                                dataIndex: 'created_at',
                                key: 'created_at',
                                render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm'),
                            },
                            {
                                title: 'Redaguota',
                                dataIndex: 'updated_at',
                                key: 'updated_at',
                                render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm'),
                            },
                            {
                                title: 'Veiksmai',
                                key: 'actions',
                                render: (_text, record) => (
                                    <Space>
                                        <Button icon={<EditOutlined />} onClick={() => handleEdit(record.id)}>Redaguoti</Button>
                                        <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record)}>Pašalinti</Button>
                                    </Space>
                                ),
                            },
                        ]} />
                </Card>
            </Main>
            <Modal
                title={singleExam?.id ? 'Atnaujinti egzaminą' : 'Sukurti naują egzaminą'}
                visible={isModalVisible}
                onOk={handleCreate}
                onCancel={handleCancel}
                okText={singleExam?.id ? 'Atnaujinti' : 'Sukurti'}
                className="exam-modal"
                cancelText="Atšaukti">
                <Form layout="vertical" form={form}>
                    <Form.Item
                        label="Pavadinimas"
                        name="title"
                        rules={[
                            { required: true, message: 'Įveskite pavadinimą' },
                            { max: 191, message: 'Pavadinimas negali viršyti 191 simbolių' },
                        ]}>
                        <Input maxLength={191} showCount />
                    </Form.Item>

                    <Form.Item
                        label="Kodas"
                        name="code"
                        rules={[
                            { required: true, message: 'Įveskite kodą' },
                            { max: 191, message: 'Kodas negali viršyti 191 simbolių' },
                        ]}>
                        <Input maxLength={191} showCount />
                    </Form.Item>

                    <Form.Item
                        label="Tipas"
                        name="type"
                        rules={[{ required: true, message: 'Pasirinkite tipą' }]}>
                        <Select placeholder="Pasirinkite tipą">
                            {examTypeOptions.map(opt => (
                                <Select.Option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="is_active" valuePropName="checked">
                                <Checkbox>Aktyvus</Checkbox>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="is_steam" valuePropName="checked">
                                <Checkbox>STEAM</Checkbox>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
            <Modal
                title="Filtruoti egzaminus"
                visible={isFilterVisible}
                onCancel={() => setIsFilterVisible(false)}
                onOk={() => {
                    setIsFilterVisible(false);
                }}
                okText="Uždaryti"
                cancelText="Atšaukti"
                footer={[
                    <Button key="clear"
                        onClick={() => {
                            setFilters({
                                type: [],
                                is_active: [],
                                is_steam: [],
                            });
                            setIsFilterVisible(false);
                        }}>
                        Išvalyti filtrus
                    </Button>,
                    <Button key="submit"
                        type="primary"
                        onClick={() => {
                            setIsFilterVisible(false);
                        }}>
                        Filtruoti
                    </Button>,
                ]}>
                <Form layout="vertical">
                    <Form.Item label="Tipas">
                        <Select
                            mode="multiple"
                            allowClear
                            value={filters.type}
                            onChange={(value) => setFilters({ ...filters, type: value })}>
                            {examTypeOptions.map(opt => (
                                <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="Aktyvus">
                        <Select
                            mode="multiple"
                            allowClear
                            value={filters.is_active}
                            onChange={(value) => setFilters({ ...filters, is_active: value })}>
                            <Select.Option value>Taip</Select.Option>
                            <Select.Option value={false}>Ne</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item label="STEAM">
                        <Select
                            mode="multiple"
                            allowClear
                            value={filters.is_steam}
                            onChange={(value) => setFilters({ ...filters, is_steam: value })}>
                            <Select.Option value>Taip</Select.Option>
                            <Select.Option value={false}>Ne</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default ExamsListing;
